/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { accessAPI, conIva, formatFecha, formatMonto } from "../../../shared/index.js";
import { Boton, Titulo, useFuncionalidad, useIva } from "../../../shared/index.js";
import { Toast } from "primereact/toast";
import {
  Sidebar,
  tituloBase,
  TablaFiltrable,
  TarjetaCompacta,
  usePermiso
} from "../../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Tooltip } from "primereact/tooltip";
import { Skeleton } from "primereact/skeleton";
import FormularioNuevoProcesoCliente from "./FormularioNuevoProcesoCliente.js";
import FormularioFamiliaJuguetes from "./FormularioFamiliaJuguetes.js";
import SidebarFactura from "../../facturacion/SidebarFactura";
import MuestrasCliente from "../../muestra/lista/MuestrasCliente";
import ImportacionesCliente from "../../importaciones/ImportacionesCliente";
import useConPenta from "../../importaciones/useConPenta";

const PROCESOS_SEARCH_FIELDS = ["nombre", "nombrefantasia"];
const PLANIFICACIONES_SEARCH_FIELDS = [
  "proceso.nombre",
  "proceso.nombrefantasia",
  "actividadtipo.nombre",
  "comentario"
];
const PROPUESTAS_SEARCH_FIELDS = ["solicitud.nombrefantasia", "_procesos"];

export default function ProcesosCliente() {
  const iva = useIva();
  // Esta opción crea el PRIMER proceso de juguetes de un cliente, así que no
  // puede depender de que el cliente ya esté clasificado como juguetero: sería
  // un círculo. Depende de si el organismo certifica juguetes.
  const conJuguetesDni = useFuncionalidad("JUGUETES_DNI");
  const conPenta = useConPenta();
  const [procesos, setProcesos] = useState();
  const [procesosNoActivos, setProcesosNoActivos] = useState();
  const [datosCliente, setDatosCliente] = useState();
  const [sidebarNuevoProceso, setSidebarNuevoProceso] = useState(false);
  const [sidebarFamiliaJuguetes, setSidebarFamiliaJuguetes] = useState(false);
  const [propuestas, setPropuestas] = useState();
  const [hallazgos, setHallazgos] = useState();
  const [facturas, setFacturas] = useState();
  const [facturaIdSeleccionada, setFacturaIdSeleccionada] = useState(null);
  const [certificadosExtendidos, setCertificadosExtendidos] = useState([]);
  const [planificaciones, setPlanificaciones] = useState();

  const toast = useRef(null);
  const navigate = useNavigate();

  const { clienteId } = useParams();
  const { puedeVer: puedeVerFacturacion } = usePermiso("admin.facturacion");

  // Abre el destino de una tarjeta de pendientes; con cmd/ctrl, en otra pestaña
  function abrirPendiente(event, url) {
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  }

  function cargarFacturasPendientes() {
    // Sin permiso de facturación no se consultan ni se muestran
    if (!puedeVerFacturacion) return;
    accessAPI(
      "GET",
      `admin/propuesta/cliente/${clienteId}/facturaspendientes`,
      null,
      (respuesta) => setFacturas(respuesta),
      () => {}
    );
  }

  // Aparte del resto: los permisos llegan después del primer render. Sin
  // permiso de facturación no se consultan las propuestas ni las facturas.
  useEffect(() => {
    if (puedeVerFacturacion) {
      accessAPI(
        "GET",
        `admin/propuesta/cliente/${clienteId}`,
        null,
        (respuesta) => setPropuestas(respuesta.map((p) => ({
          ...p,
          _procesos: p.solicitud?.proceso?.map((pr) => pr.nombre).join(", ") || "",
        }))),
        () => {}
      );
    }
    cargarFacturasPendientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, puedeVerFacturacion]);

  // Cuando carga el componente va a buscar los procesos de cliente seleccionado
  useEffect(() => {
    accessAPI(
      "GET",
      `admin/proceso/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setProcesos(respuesta.procesosActivos);
        setProcesosNoActivos(respuesta.procesosNoActivos);
        setDatosCliente(respuesta.cliente);
        setCertificadosExtendidos(respuesta.certificadosExtendidos || []);
        document.title = `Procesos ${respuesta.cliente.razonsocial}`;
      },
      (respuesta) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );


    accessAPI(
      "GET",
      `admin/hallazgo/cliente/${clienteId}`,
      null,
      (respuesta) => setHallazgos(respuesta),
      () => {}
    );

    accessAPI(
      "GET",
      `admin/planificacion/cliente/${clienteId}`,
      null,
      (respuesta) => setPlanificaciones(respuesta),
      () => {}
    );

    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Toast ref={toast} position="top-center" />
      <Sidebar
        visible={sidebarNuevoProceso}
        onHide={() => setSidebarNuevoProceso(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo proceso</Titulo>
        <FormularioNuevoProcesoCliente
          clienteId={clienteId}
          onProcesoCreado={(respuesta) => {
            setSidebarNuevoProceso(false);
            navigate(`/proceso/${respuesta.id}`);
          }}
        />
      </Sidebar>
      <Sidebar
        visible={sidebarFamiliaJuguetes}
        onHide={() => setSidebarFamiliaJuguetes(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <FormularioFamiliaJuguetes
          clienteId={clienteId}
          toast={toast}
        />
      </Sidebar>
      {!procesos && (
        <>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "2em",
              marginBottom: "1em"
            }}
          >
            <Skeleton width="20rem" height="2rem" />
            <div css={{ display: "flex", gap: "0.5em" }}>
              <Skeleton width="10rem" height="2.5rem" />
              <Skeleton width="10rem" height="2.5rem" />
            </div>
          </div>
          <Card css={{ marginBottom: "1em" }}>
            <Skeleton
              width="10rem"
              height="1.5rem"
              css={{ marginBottom: "1em" }}
            />
            <Skeleton
              width="100%"
              height="2.5rem"
              css={{ marginBottom: "0.5em" }}
            />
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                width="100%"
                height="3rem"
                css={{ marginBottom: "0.3em" }}
              />
            ))}
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <Skeleton width="12rem" height="1.5rem" />
          </Card>
          <Card css={{ marginBottom: "1em" }}>
            <Skeleton width="12rem" height="1.5rem" />
          </Card>
          <Card>
            <Skeleton width="12rem" height="1.5rem" />
          </Card>
        </>
      )}
      {procesos && (
        <>
          <Titulo
            botones={
              <>
                <Boton
                  callback={() => setSidebarNuevoProceso(true)}
                  menu={
                    conJuguetesDni
                      ? [
                          {
                            etiqueta: "Certificado de familia de juguetes",
                            callback: () => setSidebarFamiliaJuguetes(true)
                          }
                        ]
                      : undefined
                  }
                >
                  Nuevo proceso
                </Boton>
              </>
            }
          >
            {datosCliente.razonsocial} - Procesos
          </Titulo>
          {(() => {
            const propuestasPendientes = propuestas?.filter(
              (p) => p.fechaemision && !p.fechaaceptacion && !p.archivada
            ) || [];
            const hallazgosAbiertos = hallazgos || [];
            const facturasPendientes = facturas || [];
            if (propuestasPendientes.length === 0 && hallazgosAbiertos.length === 0 && facturasPendientes.length === 0)
              return null;
            return (
              <div css={{ marginBottom: "1em" }}>
                <Titulo etiqueta="Importante" error>Pendientes</Titulo>
                <div className="grid">
                  {propuestasPendientes.map((p) => (
                    <TarjetaCompacta
                      key={`p-${p.id}`}
                      icono="pi pi-file-edit"
                      etiqueta="Propuesta comercial"
                      titulo={p.solicitud?.nombrefantasia}
                      lineas={[
                        "Pendiente de aceptación",
                        `Emitida el ${formatFecha(p.fechaemision)}`
                      ]}
                      // Abrir la propuesta pide permiso de facturación
                      onClick={
                        puedeVerFacturacion
                          ? (event) =>
                              abrirPendiente(
                                event,
                                `/propuesta/${p.id}?volver=/clientes/${clienteId}/procesos`
                              )
                          : undefined
                      }
                    />
                  ))}
                  {hallazgosAbiertos.map((h) => (
                    <TarjetaCompacta
                      key={`h-${h.id}`}
                      icono="pi pi-exclamation-triangle"
                      etiqueta={h.hallazgoTipo?.nombre || "Hallazgo"}
                      titulo={h.resumen || h.actividad?.proceso?.nombrefantasia}
                      lineas={[`Creado el ${formatFecha(h.fechaCreacion)}`]}
                      onClick={(event) =>
                        abrirPendiente(
                          event,
                          `/proceso/${h.actividad.proceso.id}/actividades/${h.actividadId}`
                        )
                      }
                    />
                  ))}
                  {facturasPendientes.map((f) => {
                    const items = (f.procesos || [])
                      .map((p) => p.descripcion)
                      .filter(Boolean);
                    const lista =
                      items.length > 0
                        ? items
                        : (f.propuestas || [])
                            .map((p) => p.solicitud?.nombrefantasia)
                            .filter(Boolean);
                    return (
                      <TarjetaCompacta
                        key={`f-${f.id}`}
                        icono="pi pi-receipt"
                        etiqueta="Factura pendiente de pago"
                        titulo={f.numeroFactura || "Factura"}
                        lineas={[
                          lista.length > 0 &&
                            `${lista.slice(0, 2).join(", ")}${
                              lista.length > 2 ? "…" : ""
                            }`,
                          f.masIva
                            ? `${f.moneda?.simbolo || ""} ${formatMonto(
                                conIva(f.monto, iva)
                              )} (IVA incluido)`
                            : `${f.moneda?.simbolo || ""} ${formatMonto(f.monto)}`
                        ]}
                        onClick={() => setFacturaIdSeleccionada(f.id)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>Procesos activos</Titulo>
            <TablaFiltrable
              value={procesos}
              searchFields={PROCESOS_SEARCH_FIELDS}
              nombreArchivo="procesos_activos"
              onRowClick={(e) => {
                if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
                  window.open(`/proceso/${e.data.id}`, "_blank");
                } else {
                  navigate(`/proceso/${e.data.id}`);
                }
              }}
              rowClassName={() => "cursor-pointer"}
              rows={10}
            >
              <Column
                header="Número"
                body={(proceso) => {
                  const certificado = proceso.certificado?.[0];
                  const tieneCertificado = !!certificado;
                  const esVigente = certificado?.estadocertificado?.vigentebool;
                  const esBajaDefinitiva =
                    certificado?.estadocertificado?.bajadefinitiva;
                  const colorPunto = esVigente
                    ? "var(--verde)"
                    : esBajaDefinitiva
                      ? "var(--rojo)"
                      : "var(--amarillo)";
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em"
                      }}
                    >
                      {tieneCertificado && (
                        <span
                          css={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: colorPunto,
                            flexShrink: 0
                          }}
                        />
                      )}
                      {proceso.nombre}
                      {certificado?.extensioncertificado?.length > 0 && (
                        <Tag value="Extendido" severity="info" />
                      )}
                      {proceso.tieneAnalisisIAPendiente && (
                        <span
                          title="Análisis de IA pendiente"
                          css={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "18px",
                            height: "18px",
                            padding: "0 5px",
                            borderRadius: "9px",
                            backgroundColor: "var(--rojo)",
                            color: "white",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            flexShrink: 0
                          }}
                        >
                          1
                        </span>
                      )}
                    </div>
                  );
                }}
              />
              <Column field="nombrefantasia" header="Nombre" />
              <Column
                header="Norma"
                body={(proceso) =>
                  proceso.norma ? proceso.norma.numero : "Sin norma"
                }
              />
            </TablaFiltrable>
          </Card>

          {certificadosExtendidos.length > 0 && (
            <Card css={{ marginBottom: "1em" }}>
              <Titulo>Certificados extendidos hacia esta empresa</Titulo>
              <TablaFiltrable conFiltros={false}
                value={certificadosExtendidos}
                nombreArchivo="certificados_extendidos"
                onRowClick={(e) => {
                  // El cert pertenece al titular, pero navegamos desde el cliente
                  // extendido: pasamos ?volver para que el botón "volver" del
                  // certificado regrese a ESTE cliente, no al dueño del cert.
                  const url = `/proceso/${e.data.procesoId}/certificado/${e.data.certificadoId}?volver=/clientes/${clienteId}/procesos`;
                  if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
                    window.open(url, "_blank");
                  } else {
                    navigate(url);
                  }
                }}
                rowClassName={() => "cursor-pointer"}
                paginator
                rows={10}
              >
                <Column
                  header="Número"
                  body={(ext) => {
                    const esVigente = ext.estadocertificado?.vigentebool;
                    const esBajaDefinitiva = ext.estadocertificado?.bajadefinitiva;
                    const colorPunto = esVigente
                      ? "var(--verde)"
                      : esBajaDefinitiva
                        ? "var(--rojo)"
                        : "var(--amarillo)";
                    return (
                      <div
                        css={{ display: "flex", alignItems: "center", gap: "0.5em" }}
                      >
                        <span
                          css={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: colorPunto,
                            flexShrink: 0
                          }}
                        />
                        {ext.certificadoNumero || ext.nombre}
                      </div>
                    );
                  }}
                />
                <Column header="Titular" body={(ext) => ext.titular} />
                <Column header="Tipo" body={(ext) => ext.certificadotipo || "—"} />
                <Column
                  header="Norma"
                  body={(ext) => ext.norma || "Sin norma"}
                />
              </TablaFiltrable>
            </Card>
          )}

          <Accordion
            transitionOptions={{ timeout: 0 }}
            css={{ marginBottom: "1em", "& .p-accordion-header-link": { color: "black" }, "& .p-toggleable-content": { transition: "none !important" } }}
          >
            <AccordionTab header="Muestras">
              <MuestrasCliente clienteId={clienteId} />
            </AccordionTab>
          </Accordion>
        </>
      )}

      {conPenta && <ImportacionesCliente clienteId={clienteId} />}

      {planificaciones && planificaciones.length > 0 && (
        <Accordion
          transitionOptions={{ timeout: 0 }}
          css={{ marginBottom: "1em", "& .p-accordion-header-link": { color: "black" }, "& .p-toggleable-content": { transition: "none !important" } }}
        >
          <AccordionTab header={`Planificaciones (${planificaciones.length})`}>
            <TablaFiltrable
              value={planificaciones}
              searchFields={PLANIFICACIONES_SEARCH_FIELDS}
              nombreArchivo="planificaciones"
              onRowClick={(e) => {
                const url = `/proceso/${e.data.proceso.id}/actividades?volver=/clientes/${clienteId}/procesos`;
                e.originalEvent.metaKey || e.originalEvent.ctrlKey
                  ? window.open(url, "_blank")
                  : navigate(url);
              }}
              stripedRows
              emptyMessage="No hay planificaciones"
              css={{
                "& .p-datatable-tbody > tr": {
                  cursor: "pointer"
                }
              }}
            >
              <Column
                header="Proceso"
                body={(planificacion) =>
                  `${planificacion.proceso.nombre} - ${planificacion.proceso.nombrefantasia}`
                }
              />
              <Column
                header="Actividad"
                body={(planificacion) => planificacion.actividadtipo.nombre}
              />
              <Column
                body={(planificacion) =>
                  planificacion.comentario ? (
                    <>
                      <Tooltip target={`.comentario-planificacion-${planificacion.id}`} />
                      <i
                        className={`pi pi-info-circle comentario-planificacion-${planificacion.id}`}
                        data-pr-tooltip={planificacion.comentario}
                        data-pr-position="top"
                        css={{
                          color: "var(--amarillo)",
                          fontSize: "1.2rem",
                          cursor: "pointer"
                        }}
                      />
                    </>
                  ) : null
                }
                style={{ width: "5em" }}
                bodyStyle={{ textAlign: "center" }}
              />
              <Column
                header="Vencimiento"
                body={(planificacion) => {
                  const hoy = new Date();
                  hoy.setHours(0, 0, 0, 0);
                  const fecha = new Date(planificacion.fecha);
                  fecha.setHours(0, 0, 0, 0);
                  const dias = Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24));

                  if (dias < 0) {
                    return <Tag value={`${Math.abs(dias)} atrás`} severity="danger" />;
                  }
                  if (dias === 0) {
                    return <Tag value="Hoy" severity="danger" />;
                  }
                  if (dias <= 7) {
                    return <Tag value={`${dias}`} severity="warning" />;
                  }
                  return <Tag value={`${dias}`} severity="info" />;
                }}
                style={{ width: "10em" }}
                alignHeader="center"
                bodyStyle={{ textAlign: "center" }}
              />
            </TablaFiltrable>
          </AccordionTab>
        </Accordion>
      )}

      {propuestas && propuestas.length > 0 && (
        <Accordion
          transitionOptions={{ timeout: 0 }}
          css={{ marginBottom: "1em", "& .p-accordion-header-link": { color: "black" }, "& .p-toggleable-content": { transition: "none !important" } }}
        >
          <AccordionTab header="Propuestas comerciales">
            <TablaFiltrable
              value={propuestas}
              searchFields={PROPUESTAS_SEARCH_FIELDS}
              nombreArchivo="propuestas_comerciales"
              onRowClick={(e) => {
                // Abrir la propuesta pide permiso de facturación: sin él, las
                // filas se ven pero no llevan a ningún lado.
                if (!puedeVerFacturacion) return;
                if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
                  window.open(`/propuesta/${e.data.id}?volver=/clientes/${clienteId}/procesos`, "_blank");
                } else {
                  navigate(`/propuesta/${e.data.id}?volver=/clientes/${clienteId}/procesos`);
                }
              }}
              rowClassName={() => (puedeVerFacturacion ? "cursor-pointer" : "")}
              rows={10}
            >
              <Column
                header="Fecha emisión"
                body={(p) =>
                  p.fechaemision
                    ? formatFecha(p.fechaemision)
                    : "—"
                }
              />
              <Column
                header="Estado"
                body={(p) =>
                  p.fechaaceptacion ? (
                    <Tag value="Aceptada" severity="success" />
                  ) : p.fechaemision ? (
                    <Tag value="Pendiente" severity="warning" />
                  ) : (
                    <Tag value="No enviada" severity="danger" />
                  )
                }
              />
              <Column
                header="Solicitud"
                field="solicitud.nombrefantasia"
                body={(p) => p.solicitud?.nombrefantasia || "—"}
              />
              <Column
                header="Procesos"
                body={(p) => {
                  const procesos = p.solicitud?.proceso;
                  if (!procesos || procesos.length === 0) return "—";
                  return (
                    <div css={{ display: "flex", flexDirection: "column", gap: "0.25em" }}>
                      {procesos.map((pr) => (
                        <a
                          key={pr.id}
                          href={`/proceso/${pr.id}`}
                          onClick={(e) => {
                            if (!e.metaKey && !e.ctrlKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/proceso/${pr.id}`);
                            }
                          }}
                          css={{ color: "var(--principal)", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                          {pr.nombre}
                        </a>
                      ))}
                    </div>
                  );
                }}
              />
            </TablaFiltrable>
          </AccordionTab>
        </Accordion>
      )}

      {procesosNoActivos && procesosNoActivos.length > 0 && (
        <Accordion
          transitionOptions={{ timeout: 0 }}
          css={{ "& .p-accordion-header-link": { color: "black" }, "& .p-toggleable-content": { transition: "none !important" } }}
        >
          <AccordionTab header="Procesos no activos">
            <TablaFiltrable
              value={procesosNoActivos}
              searchFields={PROCESOS_SEARCH_FIELDS}
              nombreArchivo="procesos_no_activos"
              onRowClick={(e) => {
                if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
                  window.open(`/proceso/${e.data.id}`, "_blank");
                } else {
                  navigate(`/proceso/${e.data.id}`);
                }
              }}
              rowClassName={() => "cursor-pointer"}
              rows={10}
            >
              <Column
                header="Número"
                body={(proceso) => {
                  const certificado = proceso.certificado?.[0];
                  const tieneCertificado = !!certificado;
                  const esVigente = certificado?.estadocertificado?.vigentebool;
                  const esBajaDefinitiva =
                    certificado?.estadocertificado?.bajadefinitiva;
                  const colorPunto = esVigente
                    ? "var(--verde)"
                    : esBajaDefinitiva
                      ? "var(--rojo)"
                      : "var(--amarillo)";
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em"
                      }}
                    >
                      {tieneCertificado && (
                        <span
                          css={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: colorPunto,
                            flexShrink: 0
                          }}
                        />
                      )}
                      {proceso.nombre}
                      {certificado?.extensioncertificado?.length > 0 && (
                        <Tag value="Extendido" severity="info" />
                      )}
                      {proceso.tieneAnalisisIAPendiente && (
                        <span
                          title="Análisis de IA pendiente"
                          css={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "18px",
                            height: "18px",
                            padding: "0 5px",
                            borderRadius: "9px",
                            backgroundColor: "var(--rojo)",
                            color: "white",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            flexShrink: 0
                          }}
                        >
                          1
                        </span>
                      )}
                    </div>
                  );
                }}
              />
              <Column field="nombrefantasia" header="Nombre" />
              <Column
                header="Norma"
                body={(proceso) =>
                  proceso.norma ? proceso.norma.numero : "Sin norma"
                }
              />
            </TablaFiltrable>
          </AccordionTab>
        </Accordion>
      )}
      <SidebarFactura
        facturaId={facturaIdSeleccionada}
        onHide={() => setFacturaIdSeleccionada(null)}
        onAccion={cargarFacturasPendientes}
      />
    </div>
  );
}
